import {
  Component,
  computed,
  DestroyRef,
  EventEmitter,
  OnInit,
  Output,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';
import { DynamicComponentSelectorService, Option, VariableMenuItem } from '../dynamic-component-selector.service';
import { Observable, of, ReplaySubject } from 'rxjs';
import { map, shareReplay, startWith, switchMap } from 'rxjs/operators';
import { SubstitutionParameter } from '@vendasta/templates';
import { MatDialogRef } from '@angular/material/dialog';
import { UntypedFormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'email-dynamic-component-dialog',
  templateUrl: './dynamic-component-dialog.component.html',
  styleUrls: ['./dynamic-component-dialog.component.scss'],
  standalone: false,
})
export class DynamicComponentDialogComponent implements OnInit {
  @Output() formResult: EventEmitter<string | null> = new EventEmitter<string | null>();
  private readonly allowedEntryApps = ['RM', 'SM'];

  variables$: Observable<VariableMenuItem[]> = this.dynamicComponentSelectorService
    .getVariables({ includeHtml: true })
    .pipe(
      map((vars) => {
        return vars.filter((v) => {
          return (v?.options?.requiredParams || []).reduce((prev, _, i, arr) => {
            return prev && !!this[arr[i]];
          }, true);
        });
      }),
      shareReplay(1),
    );
  protected disabled: WritableSignal<boolean> = signal(true);

  protected chosenCategory: WritableSignal<VariableMenuItem | null> = signal(null);
  protected chosenParam: WritableSignal<SubstitutionParameter | null> = signal(null);
  protected chosenParamDetails: WritableSignal<string> = signal('');

  protected chosenDynamicParam$$ = new ReplaySubject<string>(1);
  protected chosenDynamicParam$: Observable<Option[]>;

  protected categoryControl = new UntypedFormControl();
  protected paramControl = new UntypedFormControl();
  protected paramDetailsControl = new UntypedFormControl();

  protected filteredCategories$: Observable<VariableMenuItem[]>;
  protected filteredParams: Signal<SubstitutionParameter[]>;
  protected filteredParamDetails$: Observable<Option[]>;

  constructor(
    private readonly dynamicComponentSelectorService: DynamicComponentSelectorService,
    public dialogRef: MatDialogRef<DynamicComponentDialogComponent, string | null>,
    private readonly translate: TranslateService,
    private destroyRef: DestroyRef,
  ) {}

  ngOnInit(): void {
    this.categoryControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef), startWith('')).subscribe((value) => {
      this.filterCategories(value);
    });

    this.paramControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef), startWith('')).subscribe((value) => {
      this.filterParams(value);
    });

    this.chosenDynamicParam$ = this.chosenDynamicParam$$.asObservable().pipe(
      switchMap((chosenDynamicParam) => {
        if (
          chosenDynamicParam === 'productName' ||
          chosenDynamicParam === 'productPreviewUrl' ||
          chosenDynamicParam === 'productUrl'
        ) {
          return this.dynamicComponentSelectorService.getProductSubstitutionVariables().pipe(
            map((apps) => {
              if (chosenDynamicParam === 'productUrl') {
                apps = apps.filter((a) => this.allowedEntryApps.includes(a.appId));
              }
              return apps.map((app) => {
                return {
                  title: app.name,
                  value: app.appId,
                };
              });
            }),
          );
        } else {
          return of([]);
        }
      }),
    );

    this.paramDetailsControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef), startWith(''))
      .subscribe((value) => {
        this.filterParamDetails(value);
      });
  }

  protected filterCategories(value?: VariableMenuItem): void {
    const query = (value?.textKey || this.categoryControl.value || '').toLowerCase();
    this.filteredCategories$ = this.variables$.pipe(
      map((vars) => vars.filter((v) => v.textKey.toLowerCase().includes(query))),
    );
  }

  protected filterParams(value?: SubstitutionParameter): void {
    const query = (value?.displayName || this.paramControl.value || '').toLowerCase();
    this.filteredParams = computed(() =>
      this.chosenCategory()?.subItems.filter((v) => v.displayName.toLowerCase().includes(query)),
    );
  }

  protected filterParamDetails(value?: Option): void {
    const query = (value?.title || this.paramDetailsControl.value || '').toLowerCase();
    this.filteredParamDetails$ = this.chosenDynamicParam$.pipe(
      map((vars) => vars.filter((v) => v.title.toLowerCase().includes(query))),
    );
  }

  protected handleCategoryChange(category: VariableMenuItem): void {
    this.chosenCategory.set(category);
    this.chosenParam.set(null);
    this.paramControl.setValue('');
    this.chosenParamDetails.set('');
  }

  protected handleParamChange(param: SubstitutionParameter): void {
    this.chosenParam.set(param);
    this.chosenParamDetails.set('');
    this.paramDetailsControl.setValue('');
    if (
      this.chosenParam().replacementTag === 'productName' ||
      this.chosenParam().replacementTag === 'productPreviewUrl' ||
      this.chosenParam().replacementTag === 'productUrl'
    ) {
      this.chosenDynamicParam$$.next(this.chosenParam().replacementTag);
      this.disabled.set(true);
    } else {
      this.disabled.set(false);
    }
  }

  protected handleParamDetailsChange(paramDetails: Option): void {
    this.chosenParamDetails.set(paramDetails.value);
    this.disabled.set(false);
  }

  add(): void {
    let replacementTag = this.chosenParam().replacementTag;
    if (this.chosenParamDetails()) {
      replacementTag = `${replacementTag}['${this.chosenParamDetails()}']`;
    }
    this.dialogRef.close('{{ ' + replacementTag + ' }}');
  }

  protected categoryDisplayName(category: VariableMenuItem): string {
    return category ? this.translate.instant(category.textKey) : '';
  }

  protected paramDisplayName(param: SubstitutionParameter): string {
    return param ? param.displayName : '';
  }

  protected paramDetailsDisplayName(paramDetails: Option): string {
    return paramDetails ? paramDetails.title : '';
  }

  close(): void {
    this.dialogRef.close(null);
  }
}
